import {Button, Checkbox, Group, Paper, Stack, Title} from '@mantine/core'
import {DataTable} from 'mantine-datatable'
import {useState} from 'react'

import {formatPrice} from 'src/lib/format-price'
import {ConfirmDialog} from 'src/ui/confirm-dialog'

import type {RentalAssetType, ThirdPartyRentalAssetOffering} from './schemas'
import {ThirdPartyRentalAssetOfferingFormModal} from './third-party-rental-asset-offering-form-modal'
import {
    useArchiveThirdPartyRentalAssetOffering,
    useCreateThirdPartyRentalAssetOffering,
    useThirdPartyRentalAssetOfferings,
    useUnarchiveThirdPartyRentalAssetOffering,
    useUpdateThirdPartyRentalAssetOffering,
} from './use-third-party-rental-asset-offerings'
import {useThirdPartyRentalCompanies} from './use-third-party-rental-companies'

type RentalAssetTypeOfferingsSectionProps = {
    rentalAssetType: RentalAssetType
}

function formatDailyRate(rate: number | null) {
    if (rate == null) {
        return '—'
    }
    return formatPrice(rate)
}

export function RentalAssetTypeOfferingsSection({
    rentalAssetType,
}: RentalAssetTypeOfferingsSectionProps) {
    const [includeArchived, setIncludeArchived] = useState(false)
    const {data: offerings = [], isLoading: offeringsLoading} = useThirdPartyRentalAssetOfferings({
        rentalAssetTypeId: rentalAssetType.id,
        includeArchived,
    })
    const {data: companies = [], isLoading: companiesLoading} = useThirdPartyRentalCompanies()
    const createOffering = useCreateThirdPartyRentalAssetOffering()
    const updateOffering = useUpdateThirdPartyRentalAssetOffering()
    const archiveOffering = useArchiveThirdPartyRentalAssetOffering()
    const unarchiveOffering = useUnarchiveThirdPartyRentalAssetOffering()
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [editingOffering, setEditingOffering] = useState<ThirdPartyRentalAssetOffering | null>(
        null,
    )
    const [archiveTarget, setArchiveTarget] = useState<ThirdPartyRentalAssetOffering | null>(null)

    const isLoading = offeringsLoading || companiesLoading

    async function handleArchiveConfirm() {
        if (archiveTarget) {
            try {
                await archiveOffering.mutateAsync(archiveTarget.id)
            } finally {
                setArchiveTarget(null)
            }
        }
    }

    async function handleUnarchive(offering: ThirdPartyRentalAssetOffering) {
        await unarchiveOffering.mutateAsync(offering.id)
    }

    return (
        <>
            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Group justify="space-between" align="center">
                        <Title order={4}>Third-party offerings</Title>
                        <Button onClick={() => setCreateModalOpen(true)}>Create offering</Button>
                    </Group>

                    <Checkbox
                        label="Include archived"
                        checked={includeArchived}
                        onChange={(event) => setIncludeArchived(event.currentTarget.checked)}
                    />

                    <DataTable
                        withTableBorder
                        withColumnBorders
                        striped
                        textSelectionDisabled
                        idAccessor="id"
                        records={offerings}
                        fetching={isLoading}
                        minHeight={offerings.length === 0 ? 120 : undefined}
                        // Empty until loaded so the faded text is not announced
                        noRecordsText={
                            !isLoading && offerings.length === 0
                                ? 'No third-party offerings for this type yet.'
                                : ''
                        }
                        columns={[
                            {
                                accessor: 'companyName',
                                title: 'Company',
                            },
                            {
                                accessor: 'defaultDailyRate',
                                title: 'Default daily rate',
                                render: (row) => formatDailyRate(row.defaultDailyRate),
                            },
                            {
                                accessor: 'archivedAt',
                                title: 'Status',
                                render: (row) => (row.archivedAt == null ? 'Active' : 'Archived'),
                            },
                            {
                                accessor: 'id',
                                title: 'Actions',
                                width: 220,
                                render: (row) => (
                                    <Group gap="xs">
                                        <Button
                                            variant="subtle"
                                            size="compact-sm"
                                            onClick={() => setEditingOffering(row)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="subtle"
                                            color={row.archivedAt ? undefined : 'red'}
                                            size="compact-sm"
                                            onClick={() =>
                                                row.archivedAt == null
                                                    ? setArchiveTarget(row)
                                                    : void handleUnarchive(row)
                                            }
                                        >
                                            {row.archivedAt == null ? 'Archive' : 'Unarchive'}
                                        </Button>
                                    </Group>
                                ),
                            },
                        ]}
                    />
                </Stack>
            </Paper>

            <ThirdPartyRentalAssetOfferingFormModal
                opened={createModalOpen}
                offering={null}
                companies={companies}
                rentalAssetTypes={[rentalAssetType]}
                lockedRentalAssetTypeId={rentalAssetType.id}
                lockedRentalAssetTypeName={rentalAssetType.name}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createOffering.mutateAsync(input)}
                onUpdate={(id, input) => updateOffering.mutateAsync({id, input})}
                onSaved={() => setCreateModalOpen(false)}
            />

            <ThirdPartyRentalAssetOfferingFormModal
                opened={editingOffering != null}
                offering={editingOffering}
                companies={companies}
                rentalAssetTypes={[rentalAssetType]}
                onClose={() => setEditingOffering(null)}
                onCreate={(input) => createOffering.mutateAsync(input)}
                onUpdate={(id, input) => updateOffering.mutateAsync({id, input})}
                onSaved={() => setEditingOffering(null)}
            />

            <ConfirmDialog
                opened={archiveTarget != null}
                title="Archive offering"
                message={`Are you sure you want to archive the offering from ${archiveTarget?.companyName ?? 'this company'}?`}
                confirmLabel="Archive"
                loading={archiveOffering.isPending}
                onConfirm={() => void handleArchiveConfirm()}
                onCancel={() => setArchiveTarget(null)}
            />
        </>
    )
}
